// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Form } from 'informed';
import moment from 'moment';

import {
  addSystemAssessment,
  updateSystemAssessment,
} from '../../../../actions/progressNoteActions';

import { PrimaryText } from '../../../Typography.jsx';
import ContentBox from '../../../ContentBox.jsx';
import { Button, PrimaryButton } from '../../../Buttons.jsx';
import DatetimeText from '../../../DatetimeText.jsx';

import EditCardiovascular from './EditCardiovascular.jsx';
import EditRespiratory from './EditRespiratory.jsx';
import EditNeurological from './EditNeurological.jsx';
import EditIntegumentary from './EditIntegumentary.jsx';
import EditSensory from './EditSensory.jsx';
import EditMusculoskeletal from './EditMusculoskeletal.jsx';
import EditGastrointestinal from './EditGastrointestinal.jsx';
import EditGenitourinary from './EditGenitourinary.jsx';
import EditPain from './EditPain.jsx';
import EditPsychosocial from './EditPsychosocial.jsx';

import styles from '../../../../styles/patientPages/progressNote/progressNote.scss';

type Props = {
  note: ?Object,
  /*
   * (noteId: string, note: Object) => {}
   */
  handleSaveNote: Function,
  /*
   * (note: Object) => {}
   */
  handleSaveNewNote: Function,
  activePatientId: string,
  activeAccount: Object,
  problemId: string,
  closePage: Function,
}

type State = {
  selectedSystem: string,
}

class EditSystemAssessmentComponent extends Component<Props, State> {
  formApi: {
    getState: Function,
    reset: Function,
    setValue: Function,
  }

  constructor(props: Props) {
    super(props);
    this.state = {
      selectedSystem: 'cardiovascular',
    };
  }

  componentDidMount() {
    this.formApi.reset();
  }

  setFormApi = (formApi: Object) => {
    this.formApi = formApi;
  }

  // Misc functions
  /* eslint-disable no-param-reassign */
  /* eslint-disable max-len */
  transformData(formValues: Object) {
    formValues.date = moment(formValues.date, 'DD-MM-YYYY, HH:mm').toISOString();
    // add active account for edit history
    const { activeAccount } = this.props;
    formValues.editor = `${activeAccount.name}, ${activeAccount.credential}`;
    formValues.editDate = moment().toISOString();
  }

  handleSystemSelect = (event: Object) => {
    const system = event.target.getAttribute('value');
    this.setState(prevState => ({
      ...prevState,
      selectedSystem: system,
    }));
  }

  handleSave = () => {
    const formData = this.formApi.getState();
    if (formData.invalid) {
      return;
    }
    if (!Object.keys(formData.touched).length) {
      this.props.closePage();
      return;
    }
    this.transformData(formData.values);
    if (!this.props.note) {
      this.props.handleSaveNewNote(this.props.activePatientId, {
        problemId: this.props.problemId,
        ...formData.values,
      });
    } else {
      this.props.handleSaveNote(this.props.activePatientId, {
        progressNoteId: this.props.note._id,
        problemId: this.props.problemId,
        ...formData.values,
      });
    }
    this.props.closePage();
  }

  // Functions to handle automatic calculations

  handleBPChange = () => {
    const cardio = this.formApi.getState().values.cardiovascular;
    cardio.rightArmSysBP = parseFloat(cardio.rightArmSysBP);
    cardio.leftArmSysBP = parseFloat(cardio.leftArmSysBP);
    cardio.rightAnkleSysBPDorsPed = parseFloat(cardio.rightAnkleSysBPDorsPed);
    cardio.rightAnkleSysBPPostTib = parseFloat(cardio.rightAnkleSysBPPostTib);
    cardio.leftAnkleSysBPDorsPed = parseFloat(cardio.leftAnkleSysBPDorsPed);
    cardio.leftAnkleSysBPPostTib = parseFloat(cardio.leftAnkleSysBPPostTib);
    let maxArm = cardio.leftArmSysBP;
    if (!maxArm || (cardio.rightArmSysBP && cardio.rightArmSysBP > maxArm)) {
      maxArm = cardio.rightArmSysBP;
    }
    let maxRightFoot = cardio.rightAnkleSysBPDorsPed;
    if (!maxRightFoot || (cardio.rightAnkleSysBPPostTib && cardio.rightAnkleSysBPPostTib > maxRightFoot)) {
      maxRightFoot = cardio.rightAnkleSysBPPostTib;
    }
    let maxLeftFoot = cardio.leftAnkleSysBPDorsPed;
    if (!maxLeftFoot || (cardio.leftAnkleSysBPPostTib && cardio.leftAnkleSysBPPostTib > maxLeftFoot)) {
      maxLeftFoot = cardio.leftAnkleSysBPPostTib;
    }
    const rightIndexScore = maxRightFoot / maxArm ? (maxRightFoot / maxArm).toFixed(2) : 'N/A';
    const leftIndexScore = maxLeftFoot / maxArm ? (maxLeftFoot / maxArm).toFixed(2) : 'N/A';
    this.formApi.setValue('cardiovascular.rightIndexScore', rightIndexScore);
    this.formApi.setValue('cardiovascular.leftIndexScore', leftIndexScore);
  }

  handleGlasgowChange = () => {
    const { neurological } = this.formApi.getState().values;
    let sum = 0;
    sum += neurological.eyeOpeningResponse ? parseInt(neurological.eyeOpeningResponse, 10) : 0;
    sum += neurological.bestMotorResponse ? parseInt(neurological.bestMotorResponse, 10) : 0;
    sum += neurological.bestVerbalResponse ? parseInt(neurological.bestVerbalResponse, 10) : 0;
    this.formApi.setValue('neurological.glasgowTotal', sum);
  }

  handleFlaccChange = () => {
    const { pain } = this.formApi.getState().values;
    let sum = 0;
    sum += pain.flaccFace ? parseInt(pain.flaccFace, 10) : 0;
    sum += pain.flaccLegs ? parseInt(pain.flaccLegs, 10) : 0;
    sum += pain.flaccActivity ? parseInt(pain.flaccActivity, 10) : 0;
    sum += pain.flaccCry ? parseInt(pain.flaccCry, 10) : 0;
    sum += pain.flaccConsole ? parseInt(pain.flaccConsole, 10) : 0;
    this.formApi.setValue('pain.flaccTotal', sum);
  }

  // Functions to reset radio buttons

  resetPulsesRadios = () => {
    this.formApi.setValue('cardiovascular.apical', null);
    this.formApi.setValue('cardiovascular.murmur', null);
  }

  resetTissuePerfusionRadios = () => {
    this.formApi.setValue('cardiovascular.capRefillLeftHand', null);
    this.formApi.setValue('cardiovascular.capRefillRightHand', null);
    this.formApi.setValue('cardiovascular.capRefillLeftFoot', null);
    this.formApi.setValue('cardiovascular.capRefillRightFoot', null);
    this.formApi.setValue('cardiovascular.mucousMembraneColor', null);
    this.formApi.setValue('cardiovascular.mucousMembraneMoisture', null);
  }

  resetOxygenationRadios = () => {
    this.formApi.setValue('respiratory.oxygenAmount', null);
    this.formApi.setValue('respiratory.oxygenDelivery', null);
  }

  resetSuctioningRadios = () => {
    this.formApi.setValue('respiratory.suctionAmount', null);
    this.formApi.setValue('respiratory.suctionColor', null);
    this.formApi.setValue('respiratory.suctionConsistency', null);
    this.formApi.setValue('respiratory.suctionOdor', null);
  }

  resetGagReflexRadios = () => {
    this.formApi.setValue('respiratory.gagReflex', null);
  }

  resetCoughRadios = () => {
    this.formApi.setValue('respiratory.coughType', null);
    this.formApi.setValue('respiratory.coughStrength', null);
  }

  resetProductiveCoughRadios = () => {
    this.formApi.setValue('respiratory.coughAmount', null);
    this.formApi.setValue('respiratory.coughColor', null);
    this.formApi.setValue('respiratory.coughConsistency', null);
    this.formApi.setValue('respiratory.coughOdor', null);
  }

  resetHandGripRadios = () => {
    this.formApi.setValue('neurological.handBilateralAssessment', null);
    this.formApi.setValue('neurological.rightHandGrip', null);
    this.formApi.setValue('neurological.leftHandGrip', null);
  }

  resetFootStrengthRadios = () => {
    this.formApi.setValue('neurological.footBilateralAssessment', null);
    this.formApi.setValue('neurological.rightFootStrength', null);
    this.formApi.setValue('neurological.leftFootStrength', null);
  }

  resetNeurologicalEvaluationRadios = () => {
    this.formApi.setValue('neurological.leftPupilReaction', null);
    this.formApi.setValue('neurological.rightPupilReaction', null);
    this.formApi.setValue('neurological.leftPupilSize', null);
    this.formApi.setValue('neurological.rightPupilSize', null);
    this.formApi.setValue('neurological.armMovement', null);
    this.formApi.setValue('neurological.legMovement', null);
    this.formApi.setValue('neurological.comaStatus', null);
  }

  resetGlasgowRadios = () => {
    this.formApi.setValue('neurological.eyeOpeningResponse', null);
    this.formApi.setValue('neurological.bestMotorResponse', null);
    this.formApi.setValue('neurological.bestVerbalResponse', null);
    this.formApi.setValue('neurological.glasgowTotal', null);
  }

  resetSeizureRadios = () => {
    this.formApi.setValue('neurological.aura', null);
    this.formApi.setValue('neurological.apnea', null);
    this.formApi.setValue('neurological.seizureIncontinence', null);
  }

  resetPupilsRadios = () => {
    this.formApi.setValue('sensory.pupilEqual', null);
    this.formApi.setValue('sensory.pupilRound', null);
    this.formApi.setValue('sensory.leftEyeReaction', null);
    this.formApi.setValue('sensory.rightEyeReaction', null);
    this.formApi.setValue('sensory.accommodation', null);
  }

  resetRomRadios = () => {
    this.formApi.setValue('musculoskeletal.leftArmROM', null);
    this.formApi.setValue('musculoskeletal.rightArmROM', null);
    this.formApi.setValue('musculoskeletal.leftLegROM', null);
    this.formApi.setValue('musculoskeletal.rightLegROM', null);
    this.formApi.setValue('musculoskeletal.gaitBalance', null);
  }

  resetPostOperativeRadios = () => {
    this.formApi.setValue('musculoskeletal.postOpLeft', null);
    this.formApi.setValue('musculoskeletal.postOpRight', null);
  }

  resetPostoperativeExtremityRadios = () => {
    this.formApi.setValue('musculoskeletal.postOpTemp', null);
    this.formApi.setValue('musculoskeletal.postOpColor', null);
    this.formApi.setValue('musculoskeletal.postOpRefill', null);
    this.formApi.setValue('musculoskeletal.postOpSense', null);
  }

  resetMovementRadios = () => {
    this.formApi.setValue('musculoskeletal.moveFingers', null);
    this.formApi.setValue('musculoskeletal.moveToes', null);
  }

  resetMouthGumsTeethRadios = () => {
    this.formApi.setValue('gastrointestinal.gumsMouth', null);
    this.formApi.setValue('gastrointestinal.teethDentures', null);
  }

  resetIntestinalRadios = () => {
    this.formApi.setValue('gastrointestinal.continenceBowel', null);
  }

  resetUrinaryRadios = () => {
    this.formApi.setValue('genitourinary.voiding', null);
    this.formApi.setValue('genitourinary.urineColor', null);
    this.formApi.setValue('genitourinary.urineChars', null);
    this.formApi.setValue('genitourinary.odor', null);
  }

  resetPainRadios = () => {
    this.formApi.setValue('pain.painNow', null);
    this.formApi.setValue('pain.painLocation', null);
    this.formApi.setValue('pain.painFreq', null);
    this.formApi.setValue('pain.painIntense', null);
    this.formApi.setValue('pain.painFaceRating', null);
  }

  resetFlaccRadios = () => {
    this.formApi.setValue('pain.flaccFace', null);
    this.formApi.setValue('pain.flaccLegs', null);
    this.formApi.setValue('pain.flaccActivity', null);
    this.formApi.setValue('pain.flaccCry', null);
    this.formApi.setValue('pain.flaccConsole', null);
    this.formApi.setValue('pain.flaccTotal', null);
  }

  resetAnxietyRadios = () => {
    this.formApi.setValue('psychosocial.objectiveEval', null);
  }

  render() {
    const data = this.props.note || {
      /* add section with empty object here */
      cardiovascular: {},
      respiratory: {},
      neurological: {},
      integumentary: {},
      sensory: {},
      musculoskeletal: {},
      gastrointestinal: {},
      genitourinary: {},
      pain: {},
      psychosocial: {},
      date: undefined,
      editors: undefined,
      editDates: [],
    };
    /* add object destructure here */
    const {
      cardiovascular,
      respiratory,
      neurological,
      integumentary,
      sensory,
      musculoskeletal,
      gastrointestinal,
      genitourinary,
      pain,
      psychosocial,
    } = data;

    const { editors, editDates } = data;
    let editHistory = '';
    if (editors) {
      for (let i = 0; i < editors.length; i++) {
        if (i === editors.length - 1) {
          editHistory += `${moment(editDates[i]).format('DD-MM-YYYY, HH:mm')} - ${editors[i]} (created) \n`;
        } else {
          editHistory += `${moment(editDates[i]).format('DD-MM-YYYY, HH:mm')} - ${editors[i]}\n`;
        }
      }
    }

    const { selectedSystem } = this.state;
    const systemSelector = (
      <ContentBox>
        <table className={styles.systemSelector}>
          <tbody>
            <tr>
              <td onClick={this.handleSystemSelect} value='cardiovascular' className={`${styles.systemButton} ${selectedSystem === 'cardiovascular' ? styles.active : ''}`}>Cardiovascular</td>
              <td onClick={this.handleSystemSelect} value='respiratory' className={`${styles.systemButton} ${selectedSystem === 'respiratory' ? styles.active : ''}`}>Respiratory</td>
              <td onClick={this.handleSystemSelect} value='neurological' className={`${styles.systemButton} ${selectedSystem === 'neurological' ? styles.active : ''}`}>Neurological</td>
              <td onClick={this.handleSystemSelect} value='integumentary' className={`${styles.systemButton} ${selectedSystem === 'integumentary' ? styles.active : ''}`}>Integumentary</td>
              <td onClick={this.handleSystemSelect} value='sensory' className={`${styles.systemButton} ${selectedSystem === 'sensory' ? styles.active : ''}`}>Sensory</td>
            </tr>
            <tr>
              <td><div onClick={this.handleSystemSelect} value='musculoskeletal' className={`${styles.systemButton} ${selectedSystem === 'musculoskeletal' ? styles.active : ''}`}>Musculoskeletal</div></td>
              <td><div onClick={this.handleSystemSelect} value='gastrointestinal' className={`${styles.systemButton} ${selectedSystem === 'gastrointestinal' ? styles.active : ''}`}>Gastrointestinal</div></td>
              <td><div onClick={this.handleSystemSelect} value='genitourinary' className={`${styles.systemButton} ${selectedSystem === 'genitourinary' ? styles.active : ''}`}>Genitourinary</div></td>
              <td><div onClick={this.handleSystemSelect} value='pain' className={`${styles.systemButton} ${selectedSystem === 'pain' ? styles.active : ''}`}>Pain/Wounds</div></td>
              <td><div onClick={this.handleSystemSelect} value='psychosocial' className={`${styles.systemButton} ${selectedSystem === 'psychosocial' ? styles.active : ''}`}>Psychosocial</div></td>
            </tr>
          </tbody>
        </table>
      </ContentBox>
    );

    return (
      <Form getApi={this.setFormApi} autoComplete='off'>
        <ContentBox>
          <div className={styles.saHeader}>System Assessments</div>
        </ContentBox>
        {systemSelector}
        <div className={selectedSystem !== 'cardiovascular' ? styles.hidden : ''}>
          <EditCardiovascular
            initCardiovascular={cardiovascular}
            handleBPChange={this.handleBPChange}
            resetPulsesRadios={this.resetPulsesRadios}
            resetTissuePerfusionRadios={this.resetTissuePerfusionRadios}
          />
        </div>
        <div className={selectedSystem !== 'respiratory' ? styles.hidden : ''}>
          <EditRespiratory
            initRespiratory={respiratory}
            resetOxygenationRadios={this.resetOxygenationRadios}
            resetSuctioningRadios={this.resetSuctioningRadios}
            resetGagReflexRadios={this.resetGagReflexRadios}
            resetCoughRadios={this.resetCoughRadios}
            resetProductiveCoughRadios={this.resetProductiveCoughRadios}
          />
        </div>
        <div className={selectedSystem !== 'neurological' ? styles.hidden : ''}>
          <EditNeurological
            initNeurological={neurological}
            handleGlasgowChange={this.handleGlasgowChange}
            resetHandGripRadios={this.resetHandGripRadios}
            resetFootStrengthRadios={this.resetFootStrengthRadios}
            resetNeurologicalEvaluationRadios={this.resetNeurologicalEvaluationRadios}
            resetGlasgowRadios={this.resetGlasgowRadios}
            resetSeizureRadios={this.resetSeizureRadios}
          />
        </div>
        <div className={selectedSystem !== 'integumentary' ? styles.hidden : ''}>
          <EditIntegumentary initIntegumentary={integumentary} />
        </div>
        <div className={selectedSystem !== 'sensory' ? styles.hidden : ''}>
          <EditSensory initSensory={sensory} resetPupilsRadios={this.resetPupilsRadios} />
        </div>
        <div className={selectedSystem !== 'musculoskeletal' ? styles.hidden : ''}>
          <EditMusculoskeletal
            initMusculoskeletal={musculoskeletal}
            resetRomRadios={this.resetRomRadios}
            resetPostOperativeRadios={this.resetPostOperativeRadios}
            resetPostoperativeExtremityRadios={this.resetPostoperativeExtremityRadios}
            resetMovementRadios={this.resetMovementRadios}
          />
        </div>
        <div className={selectedSystem !== 'gastrointestinal' ? styles.hidden : ''}>
          <EditGastrointestinal
            initGastrointestinal={gastrointestinal}
            resetMouthGumsTeethRadios={this.resetMouthGumsTeethRadios}
            resetIntestinalRadios={this.resetIntestinalRadios}
          />
        </div>
        <div className={selectedSystem !== 'genitourinary' ? styles.hidden : ''}>
          <EditGenitourinary
            initGenitourinary={genitourinary}
            resetUrinaryRadios={this.resetUrinaryRadios}
          />
        </div>
        <div className={selectedSystem !== 'pain' ? styles.hidden : ''}>
          <EditPain
            initPain={pain}
            handleFlaccChange={this.handleFlaccChange}
            resetPainRadios={this.resetPainRadios}
            resetFlaccRadios={this.resetFlaccRadios}
          />
        </div>
        <div className={selectedSystem !== 'psychosocial' ? styles.hidden : ''}>
          <EditPsychosocial
            initPsychosocial={psychosocial}
            resetAnxietyRadios={this.resetAnxietyRadios}
          />
        </div>
        <ContentBox>
          <table>
            <tbody>
              <tr>
                <td><PrimaryText>Exam Date </PrimaryText></td>
                <td><DatetimeText field='date' initialValue={data.date} /></td>
              </tr>
              <tr className={styles.editHistoryRow}>
                <td><strong>Edit History </strong></td>
                <td>{editHistory || '--'}</td>
              </tr>
            </tbody>
          </table>
          <PrimaryButton onClick={this.handleSave}>Save</PrimaryButton>
          <Button onClick={this.props.closePage}>Cancel</Button>
        </ContentBox>
      </Form>
    );
  }
}

const mapStateToProps = state => ({
  activePatientId: state.patient.data.activePatientId,
  activeAccount: state.account.activeAccount,
});

const mapDispatchToProps = dispatch => ({
  handleSaveNewNote:
    (patientId, symptomAnalysis) => dispatch(addSystemAssessment(patientId, symptomAnalysis)),
  handleSaveNote:
    (patientId, symptomAnalysis) => dispatch(updateSystemAssessment(patientId, symptomAnalysis)),
});

const EditSystemAssessment = connect(
  mapStateToProps,
  mapDispatchToProps,
)(EditSystemAssessmentComponent);

export default EditSystemAssessment;



// WEBPACK FOOTER //
// ./client/src/components/patientPages/progressNote/edit/EditSystemAssessment.jsx